import { Loading, MaintenancePage, ServerError } from '@components'
import { useWithAmpli } from '@contexts/AmpliContextProvider'
import { useEnvConfig } from '@contexts/EnvConfigProvider'
import { useLogo } from '@hooks/useLogo'
import { useOwnOrganisation } from '@hooks/useOwnOrganisation'
import { Menu } from '@router/Menu'
import * as S from '@router/styles'
import { AxiosError } from 'axios'
import { type FC, useContext, useEffect } from 'react'
import { Outlet } from 'react-router-dom'

import { AuthContext } from '../contexts/AuthContextProvider'

const isBackendUnavailable = (error: Error | null): boolean => {
	if (!error || !(error instanceof AxiosError)) {
		return false
	}
	// 502 Bad Gateway: Backend server is down or not responding
	// 503 Service Unavailable: Server is temporarily unable to handle requests (maintenance, overload)
	const status = error.response?.status
	return status === 502 || status === 503
}

export const SecureZone: FC = () => {
	const productEcosystemLogo = useLogo()
	const authContext = useContext(AuthContext)

	const { data, isLoading, error } = useOwnOrganisation()

	const envConfig = useEnvConfig()
	const withAmpli = useWithAmpli()
	useEffect(() => {
		if (data?.data?.id && !error && !isLoading) {
			withAmpli(ampli =>
				ampli.identify(authContext.user.id, {
					// @ts-expect-error
					organisationId: data.data.id,
					organisationName: data.data.name,
				})
			)
		}
	}, [authContext.user.id, data, envConfig, error, isLoading, withAmpli])

	if (isLoading) {
		return <Loading noWrapper={false} containerHeight="100vh" />
	}

	if (!data?.data || error) {
		// Show maintenance page when backend returns 502/503 (gateway/service unavailable)
		// Show server error page for other errors (e.g., 500 errors, user offline)
		if (isBackendUnavailable(error)) {
			return <MaintenancePage />
		}
		return <ServerError />
	}

	// TODO(pascal): Use co2AiCorpoUrl back for orgs that have a corp account when we have some.

	return (
		<S.Root>
			<Menu />
			<S.Content>
				<S.LogoWrapper>
					<img src={productEcosystemLogo} height={25} alt="" />
				</S.LogoWrapper>
				<Outlet />
			</S.Content>
		</S.Root>
	)
}
